import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { animation, hoverAnimation } from '../constants/_animation';
import { functions } from '../functions/_functions';
import { withTheme } from '../core/theming';
import { PropTypesRepo } from '../functions/_propTypes';

/* eslint-disable react/prop-types */

const CustomTag = React.forwardRef((props, ref) => {
  const {
    d,
    pos,
    top,
    bottom,
    left,
    right,
    justify,
    align,
    flexDir,
    flexGrow,
    flexWrap,
    order,
    m,
    p,
    rounded,
    border,
    borderColor,
    h,
    maxH,
    minH,
    w,
    minW,
    maxW,
    cursor,
    bg,
    bgImg,
    bgSize,
    bgRepeat,
    bgPos,
    textSize,
    textColor,
    textWeight,
    textAlign,
    textTransform,
    fontFamily,
    shadow,
    transition,
    transform,
    transformOrigin,
    zIndex,
    overflow,
    opacity,
    hoverBg,
    hoverTextColor,
    hoverBorderColor,
    hoverShadow,
    hoverTransform,
    focusBorderColor,
    isOpen,
    hover,
    offset,
    size,
    targetHover,
    theme,
    tag,
    whiteSpace,
    alignSelf,
    children,
    ...rest
  } = props;
  return React.createElement(`${tag}`, { ...rest, ref }, children);
});

/* eslint-enable react/prop-types */

const Div = styled(CustomTag)`
  box-sizing: border-box;
  ${(props) => props.d && functions.makeResponsive('display', props.d)};
  ${(props) => props.pos && functions.makeResponsive('position', props.pos)};
  ${(props) => props.top && functions.makeResponsive('top', props.top)};
  ${(props) => props.bottom && functions.makeResponsive('bottom', props.bottom)};
  ${(props) => props.left && functions.makeResponsive('left', props.left)};
  ${(props) => props.right && functions.makeResponsive('right', props.right)};
  ${(props) => props.justify && functions.makeResponsive('justify-content', props.justify)};
  ${(props) => props.align && functions.makeResponsive('align-items', props.align)};
  ${(props) => props.flexDir && functions.makeResponsive('flex-direction', props.flexDir)};
  ${(props) => props.flexGrow && functions.makeResponsive('flex', props.flexGrow)};
  ${(props) => props.flexWrap && functions.makeResponsive('flex-wrap', props.flexWrap)};
  ${(props) => props.order && functions.makeResponsive('order', props.order)};
  ${(props) => props.m && functions.findDirection('margin', props.m)};
  ${(props) => props.p && functions.findDirection('padding', props.p)};
  ${(props) => props.rounded && functions.findRoundedDirection(props.rounded, props.theme.rounded)};
  ${(props) => props.border && functions.findDirection('border', props.border)};
  ${(props) => props.borderColor && functions.makeResponsive('border-color', props.theme.colors[props.borderColor])};
  ${(props) => props.h && functions.makeResponsive('height', props.h)};
  ${(props) => props.maxH && functions.makeResponsive('max-height', props.maxH)};
  ${(props) => props.minH && functions.makeResponsive('min-height', props.minH)};
  ${(props) => props.w && functions.makeResponsive('width', props.w)};
  ${(props) => props.maxW && functions.makeResponsive('max-width', props.maxW)};
  ${(props) => props.minW && functions.makeResponsive('min-width', props.minW)};
  ${(props) => props.cursor && functions.makeResponsive('cursor', props.cursor)};
  ${(props) => props.bg && functions.makeResponsiveProperty(props.bg, 'background', props.theme.colors)};
  ${(props) => props.bgImg && `background-image: url(${props.bgImg})`};
  ${(props) => props.bgSize && functions.makeResponsive('background-size', props.bgSize)};
  ${(props) => props.bgRepeat && functions.makeResponsive('background-repeat', props.bgRepeat)};
  ${(props) => props.bgPos && functions.makeResponsive('background-position', props.bgPos)};
  ${(props) => props.textSize && functions.makeResponsiveFontSize(props.textSize, props.theme.textSize)};
  ${(props) => props.textColor && functions.makeResponsive('color', props.theme.colors[props.textColor])};
  ${(props) => props.textWeight && functions.makeResponsive('font-weight', props.textWeight)};
  ${(props) => props.textAlign && functions.makeResponsive('text-align', props.textAlign)};
  ${(props) => props.textTransform && functions.makeResponsive('text-transform', props.textTransform)};
  ${(props) => props.fontFamily && `font-family: ${props.theme.fontFamily[props.fontFamily]}`};
  ${(props) => props.transition && `transition: ${props.theme.transition}`};
  ${(props) => props.transform && functions.makeResponsive('transform', props.transform)};
  ${(props) => props.shadow && functions.makeResponsiveShadow(props.shadow, props.theme.shadows)};
  ${(props) => props.transformOrigin && functions.makeResponsive('transform-origin', props.transformOrigin)};
  ${(props) => props.zIndex && functions.makeResponsive('z-index', props.zIndex)};
  ${(props) => props.overflow && `overflow: ${props.overflow}`};
  ${(props) => props.opacity && functions.makeResponsive('opacity', props.opacity)};
  ${(props) => props.whiteSpace && `white-space: ${props.whiteSpace}`};
  ${(props) => props.alignSelf && `align-self: ${props.alignSelf}`};
  ${(props) => props.animation && animation(props)};

  :hover {
    ${(props) => props.hoverTransform && functions.makeResponsive('transform', props.hoverTransform)};
    ${(props) => props.hoverAnimation && hoverAnimation(props)};
    ${(props) => props.hoverBg && functions.makeResponsiveProperty(props.hoverBg, 'background', props.theme.colors)};
    ${(props) => props.hoverTextColor && functions.makeResponsive('color', props.theme.colors[props.hoverTextColor])};
    ${(props) =>
      props.hoverBorderColor && functions.makeResponsive('border-color', props.theme.colors[props.hoverBorderColor])};
    ${(props) => props.hoverShadow && functions.makeResponsiveShadow(props.hoverShadow, props.theme.shadows)};
  }
`;

/* eslint-disable react/require-default-props */

Div.propTypes = {
  tag: PropTypes.string,
  d: PropTypesRepo.d,
  pos: PropTypesRepo.pos,
  top: PropTypesRepo.top,
  bottom: PropTypesRepo.bottom,
  left: PropTypesRepo.left,
  right: PropTypesRepo.right,
  justify: PropTypesRepo.justify,
  align: PropTypesRepo.align,
  flexDir: PropTypesRepo.flexDir,
  flexGrow: PropTypesRepo.flexGrow,
  flexWrap: PropTypesRepo.flexWrap,
  order: PropTypesRepo.order,
  m: PropTypesRepo.m,
  p: PropTypesRepo.p,
  rounded: PropTypesRepo.rounded,
  border: PropTypesRepo.border,
  borderColor: PropTypesRepo.borderColor,
  h: PropTypesRepo.h,
  maxH: PropTypesRepo.maxH,
  minH: PropTypesRepo.minH,
  w: PropTypesRepo.w,
  maxW: PropTypesRepo.maxW,
  minW: PropTypesRepo.minW,
  cursor: PropTypesRepo.cursor,
  bg: PropTypesRepo.bg,
  bgImg: PropTypesRepo.bgImg,
  bgSize: PropTypesRepo.bgSize,
  bgRepeat: PropTypesRepo.bgRepeat,
  bgPos: PropTypesRepo.bgPos,
  textSize: PropTypesRepo.textSize,
  textColor: PropTypesRepo.textColor,
  textWeight: PropTypesRepo.textWeight,
  textAlign: PropTypesRepo.textAlign,
  textTransform: PropTypesRepo.textTransform,
  fontFamily: PropTypesRepo.fontFamily,
  transition: PropTypesRepo.transition,
  shadow: PropTypesRepo.shadow,
  transformOrigin: PropTypesRepo.transformOrigin,
  zIndex: PropTypesRepo.zIndex,
  overflow: PropTypesRepo.overflow,
  opacity: PropTypesRepo.opacity,
  hoverBg: PropTypesRepo.hoverBg,
  hoverTextColor: PropTypesRepo.hoverTextColor,
  hoverBorderColor: PropTypesRepo.hoverBorderColor,
  hoverShadow: PropTypesRepo.hoverShadow,
  hoverTransform: PropTypesRepo.hoverTransform,
  whiteSpace: PropTypes.string,
  alignSelf: PropTypes.string,
};

/* eslint-enable react/require-default-props */

Div.defaultProps = {
  tag: 'div',
  whiteSpace: 'normal',
  alignSelf: 'auto',
};

export default withTheme(Div);
